import React, { useMemo, useState, createContext, useContext } from 'react';
import { APARTMENTS } from '../pages/cm/appartamentiData';
// ---------------------------------------------------------------------------
// Single source of truth for the connected platform: cleaners (team) + jobs
// (incarichi). Actions here ripple across every screen that reads this context
// (Incarichi, Team, Guadagni, Qualità, dashboard rollups).
// ---------------------------------------------------------------------------
export type Availability = 'disponibile' | 'occupato' | 'off';
export interface Cleaner {
  id: string;
  name: string;
  zones: string[];
  availability: Availability;
}
export type JobStatus = 'da_assegnare' | 'assegnata' | 'in_corso' | 'completata';
export interface Job {
  id: string;
  apartmentId: string;
  apartmentName: string;
  zone: string;
  type: string;
  date: string;
  time: string;
  cleanerId: string | null;
  cleanerName: string | null;
  status: JobStatus;
  earning: number;
  score?: number | null;
}
export function initials(name: string): string {
  return name.split(' ').map(p => p[0]).filter(Boolean).slice(0, 2).join('');
}
// Short zone label, e.g. "Milano · Navigli" -> "Navigli"
function shortZone(zone: string): string {
  const parts = zone.split('·');
  return (parts[parts.length - 1] || zone).trim();
}
const INITIAL_CLEANERS: Cleaner[] = [{
  id: 'cl1',
  name: 'Francesca N.',
  zones: ['Navigli', 'Tortona'],
  availability: 'disponibile'
}, {
  id: 'cl2',
  name: 'Luca B.',
  zones: ['Porta Romana', 'Tortona', 'Centro'],
  availability: 'occupato'
}, {
  id: 'cl3',
  name: 'Sara M.',
  zones: ['Isola', 'Centro'],
  availability: 'disponibile'
}, {
  id: 'cl4',
  name: 'Giulia R.',
  zones: ['Navigli', 'Isola'],
  availability: 'off'
}];
function cleanerIdByName(name: string): string | null {
  const c = INITIAL_CLEANERS.find(cl => cl.name === name);
  return c ? c.id : null;
}
const INITIAL_JOBS: Job[] = [{
  id: 'j1',
  apartmentId: 'a1',
  apartmentName: 'Loft Navigli',
  zone: 'Navigli',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '09:30',
  cleanerId: cleanerIdByName('Francesca N.'),
  cleanerName: 'Francesca N.',
  status: 'in_corso',
  earning: 45
}, {
  id: 'j2',
  apartmentId: 'a2',
  apartmentName: 'Bilo Porta Romana',
  zone: 'Porta Romana',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '11:00',
  cleanerId: cleanerIdByName('Luca B.'),
  cleanerName: 'Luca B.',
  status: 'assegnata',
  earning: 42
}, {
  id: 'j3',
  apartmentId: 'a3',
  apartmentName: 'Studio Isola',
  zone: 'Isola',
  type: 'Pulizia turnover',
  date: 'Oggi',
  time: '14:00',
  cleanerId: null,
  cleanerName: null,
  status: 'da_assegnare',
  earning: 40
}, {
  id: 'j4',
  apartmentId: 'a4',
  apartmentName: 'Trilo Centro',
  zone: 'Centro',
  type: 'Deep cleaning',
  date: 'Ieri',
  time: '10:00',
  cleanerId: cleanerIdByName('Sara M.'),
  cleanerName: 'Sara M.',
  status: 'completata',
  earning: 80,
  score: 94
}, {
  id: 'j5',
  apartmentId: 'a1',
  apartmentName: 'Loft Navigli',
  zone: 'Navigli',
  type: 'Pulizia turnover',
  date: 'Ieri',
  time: '15:00',
  cleanerId: cleanerIdByName('Francesca N.'),
  cleanerName: 'Francesca N.',
  status: 'completata',
  earning: 45,
  score: 90
}];
interface PlatformContextValue {
  cleaners: Cleaner[];
  jobs: Job[];
  addCleaner: (c: Omit<Cleaner, 'id'>) => void;
  assignCleaner: (jobId: string, cleanerId: string) => void;
  startJob: (jobId: string) => void;
  completeJob: (jobId: string, score?: number) => void;
  // Generate planned cleaning jobs when a contract is formalized.
  generateContractJobs: (input: {
    apartmentId?: string;
    apartmentName?: string;
    zone: string;
    count: number;
    type?: string;
    earning: number;
  }) => number;
  // Derived helpers
  cleanerById: (id: string | null | undefined) => Cleaner | undefined;
  jobsForCleaner: (cleanerId: string) => Job[];
  jobsForApartment: (apartmentId: string) => Job[];
  cleanerAvgScore: (cleanerName: string) => number | null;
  activeLoad: (cleanerId: string) => number;
}
const PlatformContext = createContext<PlatformContextValue | undefined>(undefined);
export function PlatformProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [cleaners, setCleaners] = useState<Cleaner[]>(INITIAL_CLEANERS);
  const [jobs, setJobs] = useState<Job[]>(INITIAL_JOBS);
  const addCleaner = (c: Omit<Cleaner, 'id'>) => {
    setCleaners(prev => [...prev, {
      ...c,
      id: `cl-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
    }]);
  };
  const assignCleaner = (jobId: string, cleanerId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      cleanerId,
      cleanerName: cleaners.find(c => c.id === cleanerId)?.name ?? null,
      status: j.status === 'da_assegnare' ? 'assegnata' : j.status
    } : j));
  };
  const startJob = (jobId: string) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'in_corso'
    } : j));
  };
  const completeJob = (jobId: string, score?: number) => {
    setJobs(prev => prev.map(j => j.id === jobId ? {
      ...j,
      status: 'completata',
      score: score ?? j.score ?? null
    } : j));
  };
  const generateContractJobs: PlatformContextValue['generateContractJobs'] = ({
    apartmentId,
    apartmentName,
    zone,
    count,
    type = 'Pulizia turnover',
    earning
  }) => {
    const z = shortZone(zone);
    const resolvedApt = apartmentId && APARTMENTS.find(a => a.id === apartmentId) || APARTMENTS.find(a => a.name === apartmentName) || APARTMENTS.find(a => shortZone(a.zone) === z);
    const aptId = resolvedApt?.id ?? apartmentId ?? `apt-${z}`;
    const aptName = resolvedApt?.name ?? apartmentName ?? `Appartamento ${z}`;
    const newJobs: Job[] = Array.from({
      length: count
    }).map((_, i) => ({
      id: `j-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 5)}`,
      apartmentId: aptId,
      apartmentName: aptName,
      zone: z,
      type,
      date: 'Da pianificare',
      time: '—',
      cleanerId: null,
      cleanerName: null,
      status: 'da_assegnare',
      earning
    }));
    setJobs(prev => [...newJobs, ...prev]);
    return count;
  };
  const cleanerById = (id: string | null | undefined) => id ? cleaners.find(c => c.id === id) : undefined;
  const jobsForCleaner = (cleanerId: string) => jobs.filter(j => j.cleanerId === cleanerId);
  const jobsForApartment = (apartmentId: string) => jobs.filter(j => j.apartmentId === apartmentId);
  const activeLoad = (cleanerId: string) => jobs.filter(j => j.cleanerId === cleanerId && (j.status === 'assegnata' || j.status === 'in_corso')).length;
  // Average CleanAI score from the static interventions history per cleaner.
  const scoreIndex = useMemo(() => {
    const map: Record<string, number[]> = {};
    APARTMENTS.forEach(a => a.interventions.forEach(i => {
      if (i.score != null) {
        map[i.cleaner] = map[i.cleaner] || [];
        map[i.cleaner].push(i.score);
      }
    }));
    // also fold in completed jobs that carry a score
    jobs.forEach(j => {
      if (j.status === 'completata' && j.score != null && j.cleanerName) {
        map[j.cleanerName] = map[j.cleanerName] || [];
      }
    });
    return map;
  }, [jobs]);
  const cleanerAvgScore = (cleanerName: string): number | null => {
    const arr = scoreIndex[cleanerName];
    if (!arr || arr.length === 0) return null;
    return Math.round(arr.reduce((s, n) => s + n, 0) / arr.length);
  };
  return <PlatformContext.Provider value={{
    cleaners,
    jobs,
    addCleaner,
    assignCleaner,
    startJob,
    completeJob,
    generateContractJobs,
    cleanerById,
    jobsForCleaner,
    jobsForApartment,
    cleanerAvgScore,
    activeLoad
  }} data-id="element-1197">
      {children}
    </PlatformContext.Provider>;
}
export function usePlatform(): PlatformContextValue {
  const ctx = useContext(PlatformContext);
  if (!ctx) {
    throw new Error('usePlatform must be used within a PlatformProvider');
  }
  return ctx;
}